import GoLiveButtonContainer from "@/components/GoLiveButtonContainer";
import PostTabs from "@/components/PostTabs";
import { useDraftPost } from "@/contexts/DraftPostContext";
import { postPost } from "@/services/postTwo";
import {
  AddShoppingCartOutlined,
  ArrowUpward,
  AttachFile,
  Close,
  Event,
  Poll,
  PushPin,
  Sms,
} from "@mui/icons-material";
import {
  AppBar,
  Avatar,
  Box,
  Container,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NewAudio from "./NewAudio";
import NewCarousel from "./NewCarousel";
import NewExperience from "./NewExperience";
import NewImage from "./NewImage";
import NewMerch from "./NewMerch";
import NewPoll from "./NewPoll";
import NewVideo from "./NewVideo";
import ScheduleButtonContainer from "./ScheduleButtonContainer";
import { useActiveSubscribers } from "@/services/data";
import { useCurrentUser } from "@/services/hooks";

export default function Post() {
  const {
    file,
    setFile,
    files,
    setFiles,
    contentType,
    setContentType,
    dt,
    setDt,
    meta,
    setMeta,
    resetDraft,
  } = useDraftPost();
  const { data: activeSubscribers } = useActiveSubscribers();
  const { data: currentUser } = useCurrentUser();

  useEffect(() => {
    if (
      contentType === "audio" ||
      contentType === "merch" ||
      contentType === "experience"
    ) {
      setMeta((prev) => ({ ...prev, notification: true }));
    }
  }, [contentType]);

  const componentMap = {
    audio: (
      <NewAudio file={file} setFile={setFile} meta={meta} setMeta={setMeta} />
    ),
    image: <NewImage file={file} meta={meta} setMeta={setMeta} />,
    carousel: <NewCarousel files={files} meta={meta} setMeta={setMeta} />,
    video: <NewVideo file={file} meta={meta} setMeta={setMeta} />,
    experience: <NewExperience meta={meta} setMeta={setMeta} />,
    poll: <NewPoll meta={meta} setMeta={setMeta} />,
    merch: <NewMerch meta={meta} setMeta={setMeta} />,
  };

  const setFileType = (file) => {
    if (file.type.includes("audio")) {
      setContentType("audio");
    } else if (file.type.includes("image") && files.length > 1) {
      setContentType("carousel");
    } else if (file.type.includes("image")) {
      setContentType("image");
    } else if (file.type.includes("video")) {
      setContentType("video");
    }
  };

  useEffect(() => {
    if (file) {
      setFileType(file);
    }
  }, [file]);

  const dtUnix = dt ? Math.floor(dt.getTime() / 1000) : null;

  const mutation = useMutation({
    mutationFn: () => postPost(contentType, meta, dtUnix),
    onSuccess: async () => {
      const scheduled = !!dt;
      resetDraft();
      if (scheduled) {
        window.location.href = "/scheduled";
      } else {
        window.location.href = `/${currentUser?.key}`;
      }
    },
    onError: (error) => {
      console.error("Error:", error);
      alert(
        "Something went wrong. Please try again or contact support@notebook.fm",
      );
    },
  });

  // reset queries

  return (
    <Container maxWidth="sm" sx={{ mt: 10 }}>
      <PageAppBar
        mutation={mutation}
        contentType={contentType}
        meta={meta}
        dt={dt}
        setDt={setDt}
        file={file}
        setFile={setFile}
      />
      <Box style={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Avatar
          src={currentUser?.meta?.profile}
          sx={{ width: 48, height: 48, mr: 1 }}
          style={{
            border: 1,
            borderColor: "#04FFFF",
            borderStyle: "solid",
            marginTop: "4px",
          }}
        />
        <TextField
          fullWidth
          multiline
          sx={{ mt: 0 }}
          autoComplete="off"
          placeholder="Include a message..."
          value={meta.text}
          onChange={(e) =>
            setMeta((prev) => ({ ...prev, text: e.target.value }))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.ctrlKey) {
              e.preventDefault();
              mutation.mutate();
            }
          }}
          margin="normal"
        />
      </Box>

      {["audio", "merch", "experience"].includes(contentType) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
        >
          <Typography variant="caption" sx={{ color: "darkgray" }}>
            SMS Subscribers ({activeSubscribers?.count || 0})
          </Typography>
          <Sms
            sx={{
              fontSize: 20,
              color: meta.notification ? "#04FFFF" : "darkgray",
            }}
          />
          <Switch
            checked={meta.notification || false}
            onChange={(e) =>
              setMeta((prev) => ({ ...prev, notification: e.target.checked }))
            }
            color="primary"
          />
        </Stack>
      )}

      {contentType === "note" && (
        <ButtonRow
          setFile={setFile}
          setContentType={setContentType}
          setFiles={setFiles}
        />
      )}

      {/* Render the selected component (audio, image, etc.) */}
      {componentMap[contentType]}

      {["image", "audio", "video"].includes(contentType) && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
          sx={{ mt: 2 }}
        >
          <Typography variant="caption" sx={{ color: "darkgray" }}>
            Featured
          </Typography>
          <PushPin
            sx={{ fontSize: 18, color: meta.featured ? "#04FFFF" : "darkgray" }}
          />
          <Switch
            checked={meta.featured || false}
            onChange={(e) =>
              setMeta((prev) => ({ ...prev, featured: e.target.checked }))
            }
            color="primary"
          />
        </Stack>
      )}

      {/* no file selected */}
      {/* {!file && } */}
      {/* <IdeasContainer text={text} setText={setText} /> */}
      <PostTabs />
    </Container>
  );
}

function PageAppBar({ mutation, contentType, meta, dt, setDt }) {
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const { resetDraft } = useDraftPost();

  const handleClose = () => {
    resetDraft();
    if (contentType === "note") {
      navigate(`/${currentUser?.key}`);
    } else {
      window.location.reload();
    }
  };

  const checkDisabled = () => {
    if (mutation.isPending) {
      return true;
    }
    if (contentType === "note") {
      return !meta.text;
    }
    if (contentType === "audio") {
      return !meta.title || !meta.key || !meta.writers;
    }
    if (contentType === "image" || contentType === "video") {
      return !meta.url;
    }
    if (contentType === "carousel") {
      return !meta.urls;
    }
    if (contentType === "poll") {
      return (
        !meta.question || !meta.options.filter((option) => option !== "").length
      );
    }

    if (contentType === "experience") {
      return !meta.title || !meta.location || !meta.when || !meta.cover;
    }
    if (contentType === "merch") {
      return !meta.title || !meta.link || !meta.price || !meta.cover;
    }
    return false;
  };

  const isDisabled = checkDisabled();

  return (
    <AppBar
      position="fixed"
      elevation={0}
      style={{ background: "rgba(19,19,19,.9)" }}
    >
      {mutation.isPending && <LinearProgress color="secondary" />}
      <Toolbar style={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ width: "80px" }}>
          <IconButton onClick={handleClose} sx={{ pl: 0 }}>
            <Close />
          </IconButton>
        </Box>
        <Typography variant="h6">
          New {contentType.charAt(0).toUpperCase() + contentType.slice(1)}
        </Typography>

        <Stack direction="row" spacing={3} alignItems="center">
          <ScheduleButtonContainer
            mutation={mutation}
            dt={dt}
            setDt={setDt}
            isDisabled={isDisabled}
          />

          <Box>
            <IconButton
              size="small"
              sx={{
                background: "#9A5AEF",
                "&.Mui-disabled": {
                  background: "rgba(255, 255, 255, 0.1)",
                },
                "&:hover": {
                  background: "#9A5AEF",
                },
              }}
              onClick={mutation.mutate}
              disabled={isDisabled}
            >
              <ArrowUpward />
            </IconButton>
          </Box>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

function ButtonRow({ setFile, setContentType, setFiles }) {
  const buttonStyle = {
    border: "1px solid #9A5AEF",
    marginRight: "20px",
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "column",
        mt: 2,
        gap: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <AttachFileButton setFile={setFile} setFiles={setFiles} />
        <Typography variant="caption">Add music, image(s), video</Typography>
      </Box>

      {/* poll */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          variant="outlined"
          style={buttonStyle}
          onClick={() => setContentType("poll")}
        >
          <Poll />
        </IconButton>
        <Typography variant="caption">Create a poll</Typography>
      </Box>

      {/* merch */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          variant="outlined"
          style={buttonStyle}
          onClick={() => setContentType("merch")}
        >
          <AddShoppingCartOutlined />
        </IconButton>
        <Typography variant="caption">Offer merch</Typography>
      </Box>

      {/* experience */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          variant="outlined"
          style={buttonStyle}
          onClick={() => setContentType("experience")}
        >
          <Event />
        </IconButton>
        <Typography variant="caption">Plan a meetup</Typography>
      </Box>

      <GoLiveButtonContainer setContentType={setContentType} />
    </Box>
  );
}

function AttachFileButton({ setFile, setFiles }) {
  const handleFileChange = (e) => {
    setFiles(e.target.files);
    setFile(e.target.files[0]);
  };

  return (
    <Box>
      <input
        accept="audio/*;image/*;video/*"
        style={{ display: "none" }}
        id="icon-button-file"
        type="file"
        multiple
        onChange={handleFileChange}
      />
      <label htmlFor="icon-button-file">
        <IconButton
          variant="outlined"
          style={{ background: "#9A5AEF", marginRight: "20px" }}
          component="span"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            document.getElementById("icon-button-file").click();
          }}
        >
          <AttachFile />
        </IconButton>
      </label>
    </Box>
  );
}
